// src/components/VirtualImplantTab.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  TextField, 
  Grid, 
  Paper, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useFormContext } from '../context/FormContext';
import api from '../api';

const Input = styled('input')({
  display: 'none',
});
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Hardcoded options from error.txt as frontend cannot read files directly.
const remarkOptionsFromFile = [
    "Implant placed apical to the crestal bone.",
    "Implant extends beyond the sinus floor.",
    "Apex of the Implant seen approximating the sinus floor.",
    "Narrow width",
    "Implant placed 5mm apical to the crestal bone.",
    "Note: The cancellous bone is less dense and to be cautious about the same"
];

const VirtualImplantTab = () => {
  const { state, actions } = useFormContext();
  
  // Virtual implant specific state
  const [viFile, setViFile] = useState(null);
  const [viType, setViType] = useState('');
  const [viCount, setViCount] = useState('');
  const [viPanImage, setViPanImage] = useState(null);
  const [vi3dImage, setVi3dImage] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [implantData, setImplantData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Length and diameter options
  const lengthOptions = ['8mm', '10mm', '11.5mm', '13mm'];
  const diameterOptions = ['3.5mm', '4.3mm', '4mm', '5mm'];

  // Initialize implant data when count changes
  useEffect(() => {
    if (viCount && viCount !== '0') {
      const count = parseInt(viCount);
      const newImplantData = Array.from({ length: count }, (_, index) => ({
        id: `VI-${index + 1}`,
        length: '',
        headDiameter: '',
        apicalDiameter: '',
        image: null,
        remarks_intro: 'Placed in 00 region.', // New field
        remarks_detail: [] // New field
      }));
      setImplantData(newImplantData);
    } else {
      setImplantData([]);
    }
  }, [viCount]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setViFile(file);
    setLoading(true);
    setError('');

    try {
      // Verify document and extract patient information
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/virtual-implant/verify', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setPatientInfo(response.data);
    } catch (err) {
      console.error('Error verifying document:', err);
      setError('Failed to verify document. Please ensure it is a valid CBCT report.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    if (type === 'panoramic') {
      setViPanImage(file);
    } else if (type === '3d') {
      setVi3dImage(file);
    }
  };

  const handleImplantImageUpload = (event, index) => {
    const file = event.target.files[0];
    if (!file) return;

    const updatedData = [...implantData];
    updatedData[index].image = file;
    setImplantData(updatedData);
  };

  const handleImplantDataChange = (index, field, value) => {
    const updatedData = [...implantData];
    updatedData[index][field] = value;
    setImplantData(updatedData);
  };

  const handleSubmit = async () => {
    if (!viFile || !viType || !viCount) {
      setError('Please fill in all required fields');
      return;
    }

    if (viCount !== '0' && (!viPanImage || !vi3dImage)) {
      setError('Please upload both panoramic and 3D view images');
      return;
    }

    if (viCount !== '0') {
      const incompleteImplants = implantData.some(implant => 
        !implant.headDiameter || !implant.apicalDiameter
      );
      if (incompleteImplants) {
        setError('Please complete all implant specifications');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('document', viFile);
      formData.append('viType', viType);
      formData.append('viCount', viCount);
      
      if (viPanImage) {
        formData.append('viPanImage', viPanImage);
      }
      if (vi3dImage) {
        formData.append('vi3dImage', vi3dImage);
      }

      // Add implant data
      implantData.forEach((implant, index) => {
        formData.append(`implants[${index}][id]`, implant.id);
        formData.append(`implants[${index}][length]`, implant.length);
        formData.append(`implants[${index}][headDiameter]`, implant.headDiameter);
        formData.append(`implants[${index}][apicalDiameter]`, implant.apicalDiameter);
        formData.append(`implants[${index}][remarks_intro]`, implant.remarks_intro);
        formData.append(`implants[${index}][remarks_detail]`, implant.remarks_detail.join('\n'));
        
        if (implant.image) {
          formData.append(`implants[${index}][image]`, implant.image);
        }
      });

      const response = await api.post('/virtual-implant/generate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
      });

      // Handle file download
      const blob = response.data;
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      
      // Generate filename
      const patientName = patientInfo?.patientName || 'Patient';
      const unitNumber = patientInfo?.unitNumber || '1';
      const date = patientInfo?.date || 'Unknown';
      const filename = `Unit${unitNumber}_${patientName}_${date}_final.docx`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(link.href);
      }, 100);

    } catch (err) {
      console.error('Error generating virtual implant report:', err);
      setError('Failed to generate virtual implant report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateViCountSentence = () => {
    if (viCount === '0') {
      return "NO VIRTUAL IMPLANTS ARE PLACED DUE TO."; 
    } else if (viCount === '1') {
      return `${viCount} virtual implant is placed from Nobel Biocare company based on the length and width of the bone available, this is just to give an idea and should not be considered final.`;
    } else {
      return `${viCount} virtual implants are placed from Nobel Biocare company based on the length and width of the bone available, this is just to give an idea and should not be considered final.`;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Add Virtual Implant
      </Typography>

      {/* File Upload Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Select Patient Report for adding Virtual Implant
        </Typography>
        
        <label htmlFor="vi-file-upload">
          <Input
            accept=".docx"
            id="vi-file-upload"
            type="file"
            onChange={handleFileUpload}
          />
          <Button variant="contained" component="span" sx={{ mb: 2 }}>
            Choose DOCX File
          </Button>
        </label>
        
        {viFile && (
          <Typography variant="body2" color="success.main">
            Selected: {viFile.name}
          </Typography>
        )}

        {loading && <CircularProgress sx={{ ml: 2 }} />}
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Patient Information Display */}
      {patientInfo && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom color="primary">
            Patient Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Name:</strong> {patientInfo.patientName}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Unit:</strong> Unit {patientInfo.unitNumber}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Date:</strong> {patientInfo.date}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Verified By:</strong> {patientInfo.verifiedBy}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Virtual Implant Configuration */}
      {patientInfo && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Virtual Implant Configuration
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Report Type</InputLabel>
                  <Select
                    value={viType}
                    onChange={(e) => setViType(e.target.value)}
                    label="Report Type"
                  >
                    <MenuItem value="">Select Type</MenuItem>
                    <MenuItem value="Maxilla">Maxilla</MenuItem>
                    <MenuItem value="Mandible">Mandible</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="VI Count"
                  value={viCount}
                  onChange={(e) => setViCount(e.target.value)}
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              
              {viCount && viCount !== '0' && (
                <>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" gutterBottom>
                      VI Panoramic View Image
                    </Typography>
                    <label htmlFor="vi-pan-image-upload">
                      <Input
                        accept="image/*"
                        id="vi-pan-image-upload"
                        type="file"
                        onChange={(e) => handleImageUpload(e, 'panoramic')}
                      />
                      <Button variant="outlined" component="span" size="small">
                        Upload Image
                      </Button>
                    </label>
                    {viPanImage && (
                      <Typography variant="caption" display="block" color="success.main">
                        {viPanImage.name}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" gutterBottom>
                      VI 3D View Image
                    </Typography>
                    <label htmlFor="vi-3d-image-upload">
                      <Input
                        accept="image/*"
                        id="vi-3d-image-upload"
                        type="file"
                        onChange={(e) => handleImageUpload(e, '3d')}
                      />
                      <Button variant="outlined" component="span" size="small">
                        Upload Image
                      </Button>
                    </label>
                    {vi3dImage && (
                      <Typography variant="caption" display="block" color="success.main">
                        {vi3dImage.name}
                      </Typography>
                    )}
                  </Grid>
                </>
              )}
            </Grid>

            {viCount && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {generateViCountSentence()}
              </Alert>
            )}
          </Paper>

          {/* Image Previews */}
          {(viPanImage || vi3dImage) && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {viPanImage && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="200"
                      image={URL.createObjectURL(viPanImage)}
                      alt="Panoramic View"
                    />
                    <CardContent>
                      <Typography variant="body2">Panoramic View</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {vi3dImage && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="200"
                      image={URL.createObjectURL(vi3dImage)}
                      alt="3D View"
                    />
                    <CardContent>
                      <Typography variant="body2">3D View</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}

          {/* Implant Specifications Table */}
          {viCount && viCount !== '0' && implantData.length > 0 && (
            <Paper sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ p: 2 }}>
                Implant Specifications
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Virtual Implant</strong></TableCell>
                      <TableCell><strong>Length</strong></TableCell>
                      <TableCell><strong>Head Diameter</strong></TableCell>
                      <TableCell><strong>Apical Diameter</strong></TableCell>
                      <TableCell><strong>Image</strong></TableCell>
                      <TableCell><strong>Remarks</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {implantData.map((implant, index) => (
                      <TableRow key={implant.id}>
                        <TableCell>{implant.id}</TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={implant.length}
                              onChange={(e) => handleImplantDataChange(index, 'length', e.target.value)}
                            >
                              <MenuItem value=""><em>None</em></MenuItem>
                              {lengthOptions.map(option => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={implant.headDiameter}
                              onChange={(e) => handleImplantDataChange(index, 'headDiameter', e.target.value)}
                            >
                              {diameterOptions.map(option => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={implant.apicalDiameter}
                              onChange={(e) => handleImplantDataChange(index, 'apicalDiameter', e.target.value)}
                            >
                              {diameterOptions.map(option => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <label htmlFor={`implant-image-${index}`}>
                            <Input
                              accept="image/*"
                              id={`implant-image-${index}`}
                              type="file"
                              onChange={(e) => handleImplantImageUpload(e, index)}
                            />
                            <Button variant="outlined" component="span" size="small">
                              Upload
                            </Button>
                          </label>
                          {implant.image && (
                            <>
                              <Typography variant="caption" display="block">
                                {implant.image.name}
                              </Typography>
                              <img 
                                src={URL.createObjectURL(implant.image)} 
                                alt={`Implant ${index + 1}`}
                                style={{ width: '60px', height: '60px', objectFit: 'cover', marginTop: '4px' }}
                              />
                            </>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 300 }}>
                          <Box sx={{ mb: 2 }}>
                            <TextField
                              value={implant.remarks_intro}
                              onChange={(e) => handleImplantDataChange(index, 'remarks_intro', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Box>
                          <FormControl size="small" fullWidth>
                          <InputLabel>Remarks</InputLabel>
                            <Select
                              multiple
                              value={implant.remarks_detail}
                              onChange={(e) => handleImplantDataChange(index, 'remarks_detail', e.target.value)}
                              input={<OutlinedInput label="Remarks" />}
                              renderValue={(selected) => selected.join(', ')}
                              MenuProps={MenuProps}
                            >
                              {remarkOptionsFromFile.map(option => (
                                <MenuItem key={option} value={option}>
                                  <Checkbox checked={implant.remarks_detail.indexOf(option) > -1} />
                                  <ListItemText primary={option} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading || !viType || !viCount}
            >
              {loading ? <CircularProgress size={24} /> : 'Add Virtual Implant Details'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default VirtualImplantTab;
